import { sva } from '@allenai/varnish-panda-runtime/css';

import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

const tableRecipe = sva({
    slots: [
        'root',
        'tableHeader',
        'tableBody',
        'row',
        'cell',
        'column',
        'resizableTableContainer',
        'columnResizer',
        'dragButton',
        'checkbox',
    ],
    base: {
        root: {
            padding: '[4.5px]',
            border: '1px solid',
            borderRadius: '[6px]',
            borderColor: 'gray.30',
            borderSpacing: '0',
            alignSelf: 'start',
            wordBreak: 'break-word',
            maxWidth: '[100%]',
            minHeight: '[100px]',
            forcedColorAdjust: 'none',
        },
        tableHeader: {
            color: 'text',
            whiteSpace: 'nowrap',
            _after: {
                content: '',
                display: 'table-row',
                height: '[2px]',
            },
            '& tr:last-child': {
                '& th': {
                    borderBottom: '1px solid',
                    borderColor: 'gray.30',
                    cursor: 'default',
                },
            },
        },
        tableBody: {
            _empty: {
                color: 'text',
                _dark: {
                    color: 'cream.100',
                },
                textAlign: 'center',
                fontStyle: 'italic',
            },
        },
        row: {
            cursor: 'default',
            position: 'relative',
            transform: 'scale(1)',
            color: 'text',
            borderRadius: '[6px]',
            _selected: {
                color: 'cream.100',
                backgroundColor: 'elements.tertiary.fill',
            },
            _disabled: {
                color: 'text.disabled',
            },
            _dragging: {
                opacity: '0.6',
                transform: 'translateZ(0)',
                backgroundColor: 'elements.tertiary.fill',
                color: 'cream.100',
            },
            _href: {
                cursor: 'pointer',
            },
        },
        column: {
            padding: '[4px 8px]',
            textAlign: 'left',
        },
        cell: {
            padding: '[4px 8px]',
            textAlign: 'left',
            transform: 'translateZ(0)',
            '&:first-child': {
                borderRadius: '[6px 0 0 6px]',
            },
            '&:last-child': {
                borderRadius: '[0 6px 6px 0]',
            },
            '& img': {
                width: '[30px]',
                height: '[30px]',
                objectFit: 'cover',
                display: 'block',
            },
        },
        resizableTableContainer: {
            maxWidth: '[400px]',
            overflow: 'auto',
            position: 'relative',
            '& .flex-wrapper': {
                display: 'flex',
                alignItems: 'center',
            },
            '& .column-name': {
                flex: '1',
                font: 'inherit',
                textAlign: 'start',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderColor: '[transparent]',
            },

            '& [role="columnheader"], & [role="grid-cell"]': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        columnResizer: {
            width: '[15px]',
            backgroundColor: 'gray.60',
            height: '[25px]',
            flex: '[0 0 auto]',
            touchAction: 'none',
            boxSizing: 'border-box',
            border: '[5px]',
            borderStyle: 'none solid',
            borderColor: '[transparent]',
            backgroundClip: 'content-box',

            // possible panda conditions
            '&[data-resizable-direction=both]': {
                cursor: 'ew-resize',
            },
            '&[data-resizable-direction=left]': {
                cursor: 'e-resize',
            },

            '&[data-resizable-direction=right]': {
                cursor: 'w-resize',
            },
            '&[data-resizing]': {
                backgroundColor: '[transparent]',
                borderColor: 'elements.tertiary.fill',
            },
        },
        dragButton: {
            border: 'none',
            color: '[inherit]',
        },
        checkbox: {},
    },
});

type TableRecipeProps = RecipeVariantProps<typeof tableRecipe>;

export { tableRecipe, TableRecipeProps as default };
export type { TableRecipeProps };
